import React, {
  type ButtonHTMLAttributes,
  type FC,
  type ReactNode,
} from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger"
  | "cancel";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style of the button
   * @default 'primary'
   */
  variant?: ButtonVariant;

  /**
   * Size of the button
   * @default 'md'
   */
  size?: ButtonSize;

  /**
   * Whether the button should take up full width of container
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Text to display during loading state
   */
  loadingText?: string;

  /**
   * Icon to display before children
   */
  leftIcon?: ReactNode;

  /**
   * Icon to display after children
   */
  rightIcon?: ReactNode;
}

export const Button: FC<ButtonProps> = ({
  children,
  className = "",
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  loadingText,
  leftIcon,
  rightIcon,
  disabled,
  ...props
}) => {
  // Base styles
  const baseStyles =
    "group relative inline-flex items-center justify-center gap-2 overflow-hidden font-mono font-bold uppercase tracking-widest transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-arti-background disabled:cursor-not-allowed disabled:opacity-50 disabled:saturate-50 disabled:shadow-none disabled:after:hidden after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-white/20 after:transition-all after:duration-300 hover:after:w-full";

  // Variant styles
  const variants: Record<ButtonVariant, string> = {
    cancel:
      "border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-400 dark:hover:bg-rose-500 dark:hover:text-arti-background focus:ring-rose-500",
    danger:
      "bg-rose-600 text-white hover:bg-rose-700 shadow-sm dark:border dark:border-rose-500 dark:bg-rose-600 dark:text-arti-background dark:hover:bg-rose-500 dark:shadow-[0_0_15px_rgba(225,29,72,0.4)] focus:ring-rose-500",
    ghost:
      "bg-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200 focus:ring-slate-500 after:hidden",
    outline:
      "border border-slate-300 bg-transparent text-slate-600 hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-600 dark:text-slate-300 dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400 focus:ring-emerald-500",
    primary:
      "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm dark:border dark:border-emerald-500 dark:bg-emerald-500 dark:text-arti-background dark:hover:bg-emerald-400 dark:shadow-[0_0_15px_rgba(34,197,94,0.4)] focus:ring-emerald-500",
    secondary:
      "border border-slate-200 bg-slate-100 text-slate-600 hover:border-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-500 dark:hover:bg-slate-700 dark:hover:text-white focus:ring-slate-500",
  };

  // Size styles
  const sizes: Record<ButtonSize, string> = {
    lg: "px-6 py-3 text-sm",
    md: "px-4 py-2 text-xs",
    sm: "px-3 py-1.5 text-[10px]",
    // Inline actions inside dense table rows — scaled to the status-pill
    // rhythm, not the standard action-surface button.
    xs: "px-2 py-1 text-[9px]",
  };

  // Width style
  const width = fullWidth ? "w-full" : "";

  return (
    <button
      className={` ${baseStyles} ${variants[variant]} ${sizes[size]} rounded-none ${width} ${className} `
        .trim()
        .replace(/\s+/g, " ")}
      disabled={disabled == true || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {loadingText ?? children}
        </>
      ) : (
        <>
          {leftIcon}
          {children}
          {rightIcon}
        </>
      )}
    </button>
  );
};
